import React, { useEffect, useMemo, useRef, useState } from "react";
import "./App.css";
import "./darkMode.css";
import "./ui.css";
import Main from "./containers/Main";
import { ThemeProvider } from "styled-components";
import { chosenTheme, materialDarkTheme } from "./theme";
import { GlobalStyles } from "./global";

/* Explicit user choice, written only when the toggle is clicked. While this
   key is absent, every page load plays the light→dark intro below. */
const THEME_PREF_KEY = "theme-preference";

/* Cross-fade duration; keep in sync with body.theme-fading in ui.css. */
const FADE_MS = 1000;

function App() {
  const [isDark, setIsDark] = useState(false);
  const fadeTimer = useRef(null);

  /* Add the slow-transition class around a theme change so every surface
     cross-fades together instead of snapping. */
  const fadeTo = (dark) => {
    document.body.classList.add("theme-fading");
    setIsDark(dark);
    clearTimeout(fadeTimer.current);
    fadeTimer.current = setTimeout(
      () => document.body.classList.remove("theme-fading"),
      FADE_MS
    );
  };

  useEffect(() => {
    const stored = localStorage.getItem(THEME_PREF_KEY);
    if (stored === "dark" || stored === "light") {
      setIsDark(stored === "dark");
      return;
    }

    // Intro: start light, then ease into dark a second after the page
    // has fully loaded. If the user toggles before the timer fires, the
    // stored preference makes the timer a no-op.
    let delayTimer = null;
    let cancelled = false;
    const scheduleDark = () => {
      if (cancelled) return;
      delayTimer = setTimeout(() => {
        if (!localStorage.getItem(THEME_PREF_KEY)) fadeTo(true);
      }, 1000);
    };
    if (document.readyState === "complete") {
      scheduleDark();
    } else {
      window.addEventListener("load", scheduleDark, { once: true });
    }
    return () => {
      cancelled = true;
      clearTimeout(delayTimer);
      window.removeEventListener("load", scheduleDark);
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle("dark-mode", isDark);
    document.body.classList.toggle("light-mode", !isDark);
  }, [isDark]);

  const activeTheme = useMemo(() => {
    if (isDark) return materialDarkTheme;
    return chosenTheme;
  }, [isDark]);

  const handleToggle = () => {
    const next = !isDark;
    localStorage.setItem(THEME_PREF_KEY, next ? "dark" : "light");
    fadeTo(next);
  };

  return (
    <ThemeProvider theme={activeTheme}>
      <>
        <GlobalStyles />
        <div>
          <Main
            theme={activeTheme}
            isDark={isDark}
            onToggleTheme={handleToggle}
          />
        </div>
      </>
    </ThemeProvider>
  );
}

export default App;
